import {
  faBan,
  faCreditCard,
  faDollarSign,
  faHourglass2,
  faSackDollar
} from '@fortawesome/free-solid-svg-icons';
import { memo, useCallback, useMemo } from 'react';

import { InternalType } from '@/types/rewardCard';
import ListCard from '@/ui/components/ListCard';
import { currencyFormatter } from '@/utils/currencies';

import { Props } from './types';

const TransactionItem = ({ personTimeZone, transaction }: Props) => {
  const {
    internal_type,
    is_pending: isPending,
    merchant_icon_url,
    merchant_name,
    transaction_at,
    transaction_amount_in_cents,
    transaction_type,
    transaction_type_category,
    message
  } = transaction;
  const isDebit = internal_type === InternalType.DEBIT;
  const isFunding = internal_type === InternalType.ACCOUNT_FUND;
  const isRefund = internal_type === InternalType.REFUND;
  const isDecline = internal_type === InternalType.DECLINE;
  const isForwardDebit = internal_type === InternalType.FORWARD_DEBIT;
  const isForwardCredit = internal_type === InternalType.FORWARD_CREDIT;

  const cardProps = useMemo(() => {
    let borderColor;
    let border = 'border-0';
    let iconColor = 'text-blue-800';

    if (isPending) {
      iconColor = 'text-gray-500';
    } else if (isDecline || isForwardDebit) {
      border = 'border-2';
      borderColor = 'border-red-600';
      iconColor = 'text-red-600';
    } else if (isRefund || isFunding || isForwardCredit) {
      border = 'border-2';
      borderColor = 'border-emerald-700';
      iconColor = 'text-emerald-700';
    }

    return {
      borderColor,
      border,
      iconColor
    };
  }, [isDecline, isForwardCredit, isForwardDebit, isFunding, isPending, isRefund]);

  const getIcon = useCallback(() => {
    if (isPending) return faHourglass2;
    else if (isDecline) return faBan;
    else if (isRefund || isForwardCredit) return faDollarSign;
    else if (isFunding) return faSackDollar;

    return merchant_icon_url ?? faCreditCard;
  }, [isDecline, isForwardCredit, isFunding, isPending, isRefund, merchant_icon_url]);

  const getTitle = useCallback(() => {
    return merchant_name || transaction_type_category;
  }, [merchant_name, transaction_type_category]);

  const getSubtitle = useCallback(() => {
    if (isPending) return 'Pending';
    else if (isDebit) return 'Charge';
    else if (isDecline) return String('Declined').concat(' - ', message ?? '');
    else if (isFunding) return 'Funds added';
    else if (isRefund) return 'Refund';
    else if ([InternalType.FORWARD_CREDIT, InternalType.FORWARD_DEBIT].includes(internal_type))
      return 'Transferred previous balance';

    return transaction_type;
  }, [
    internal_type,
    isDebit,
    isDecline,
    isFunding,
    isPending,
    isRefund,
    transaction_type,
    message
  ]);

  return (
    <ListCard
      className={`${cardProps.border} ${cardProps.borderColor}`}
      debug={transaction}
      headingActions={
        // Placing this in headingActions to keep in line with headingIcon
        <div className="self-center">
          <div
            className={`font-bold ${isDecline ? 'line-through' : ''} ${isRefund || isFunding ? 'text-emerald-800' : ''}`}>
            {`${transaction_amount_in_cents > 0 ? '+' : ''}${currencyFormatter(transaction.transaction_amount_in_cents / 100)}`}
          </div>
        </div>
      }
      headingBorder={false}
      headingIcon={getIcon()}
      headingIconClassName={cardProps.iconColor}
      subtitle={getSubtitle()}
      timestamp={{ datetime: transaction_at, time_zone: personTimeZone }}
      title={getTitle()}
    />
  );
};

export default memo(TransactionItem);
